import React, { useMemo, useState } from 'react';
import PaymentRequisitionsExportButton from '../components/PaymentRequisitionsExportButton';
import {
    Box, Button, CircularProgress, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography, TextField,
    MenuItem, Dialog, IconButton, Paper, Stack, FormControl,
    InputLabel, Select, FormControlLabel, Switch, styled, keyframes,
    SelectChangeEvent,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningIcon from '@mui/icons-material/Warning';
import {
    PaymentRequisition,
    useGetPaymentRequisitionsQuery,
    useLazyGetProcurementProcessesQuery,
    usePatchPaymentRequisitionsByIdMutation,
    useGetUsersQuery
} from '../../../redux/vmsApi';
import config from "../../../config";
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import NumbersIcon from '@mui/icons-material/Numbers';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import { getCurrentDate, getDefaultDueDate } from "../../../utils/dateUtil";
import { useRouter } from "../../../hooks/useRouter";


const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
`;

const DangerTableCell = styled(TableCell)(({ theme }) => ({
    animation: `${pulse} 1.5s infinite`,
    background: 'linear-gradient(45deg, #ff4d4d, #cc0000)',
    color: theme.palette.common.white,
    fontWeight: 'bold',
    textAlign: 'right',
    padding: theme.spacing(1.5),
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
}));

type ReviewedAtPeriod = '' | 'today' | 'week' | 'month' | 'quarter' | 'year';

const NOT_REVIEWED_OPTION = '__NOT_REVIEWED__';

const getReviewedAtRange = (period: ReviewedAtPeriod) => {
    if (!period) {
        return null;
    }

    const now = new Date();
    const start = new Date(now);
    const end = new Date(now);

    if (period === 'today') {
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        return { start, end };
    }

    if (period === 'week') {
        const day = now.getDay();
        const diffToMonday = day === 0 ? -6 : 1 - day;
        start.setDate(now.getDate() + diffToMonday);
        start.setHours(0, 0, 0, 0);
        end.setTime(start.getTime());
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        return { start, end };
    }

    if (period === 'month') {
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end.setMonth(now.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        return { start, end };
    }

    if (period === 'quarter') {
        const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
        start.setMonth(quarterStartMonth, 1);
        start.setHours(0, 0, 0, 0);
        end.setMonth(quarterStartMonth + 3, 0);
        end.setHours(23, 59, 59, 999);
        return { start, end };
    }

    start.setMonth(0, 1);
    start.setHours(0, 0, 0, 0);
    end.setMonth(11, 31);
    end.setHours(23, 59, 59, 999);
    return { start, end };
};

const PaymentRequisitionList: React.FC = () => {
    const router = useRouter();

    const [filters, setFilters] = useState({
        status: '',
        reviewedByEmail: '',
        notReviewedOnly: true,
        reviewedAtPeriod: '' as ReviewedAtPeriod,
        amount: '',
        supplierName: '',
        paymentMethod: '',
        paymentType: '',
        development: '',
        project: '',
        department: '',
        reqNumber: '',
        purchaseOrderNumber: '',
        fromDate: '2025-01-06',
        toDate: getCurrentDate(),
        isOutstandingPayment: false,
        currency: '',
        reportByEmail: false,
        reportName: ''
    });
    const [triggerProcurementProcess] = useLazyGetProcurementProcessesQuery();
    const [openDialog, setOpenDialog] = useState<string | null>(null);
    const [currentOpenRow, setCurrentOpenRow] = useState<string | null>(null);
    const [reportByEmailEnabled, setReportByEmailEnabled] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [apiParams, setApiParams] = useState({ ...filters, skip: 0, limit: 10 });

    const { data: users } = useGetUsersQuery();

    const financeManagers = useMemo(
        () =>
            (users ?? []).filter(user =>
                Array.isArray(user.roles) && user.roles.includes('finance-manager')
            ),
        [users]
    );

    const { data: paymentRequisitions, isLoading, isError } =
        useGetPaymentRequisitionsQuery(apiParams);

    const handleFilterChange = (field: string, value: string | boolean) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleApplyFilters = () => {
        setApiParams({
            ...filters,
            reviewedByEmail:
                filters.reviewedByEmail === NOT_REVIEWED_OPTION ? '' : filters.reviewedByEmail,
            notReviewedOnly: filters.reviewedByEmail === NOT_REVIEWED_OPTION,
            skip: 0,
            limit: pageSize,
        });
        setPage(0);
        setReportByEmailEnabled(false);
        handleFilterChange('reportName', '');
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        setApiParams((prevParams) => ({
            ...prevParams,
            skip: newPage * pageSize,
        }));
    };

    const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
        const newSize = Number(event.target.value);
        setPageSize(newSize);
        setPage(0);
        setApiParams((prevParams: any) => ({
            ...prevParams,
            limit: newSize,
            skip: 0,
        }));
    };

    //form patching
    const [updatePaymentReq] = usePatchPaymentRequisitionsByIdMutation();
    const handleFormChange = (field: keyof PaymentRequisition, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [formData, setFormData] = useState<Partial<PaymentRequisition>>({
        paymentMethod: '',
        referenceNumber: '',
        amount: 0,
        rate: 1,
        dueDate: getDefaultDueDate(),
        status: 'Awaiting Approval',
        priority: 0,
        swap: false
    });
    const [currentPaymentReq, setCurrentPaymentReq] = useState<PaymentRequisition | null>(null);
    const selectedPurchaseOrder = currentPaymentReq?.purchaseOrder;
    const paymentCurrency = selectedPurchaseOrder?.quotation?.currency || currentPaymentReq?.currency || 'USD';
    const paymentReferenceValue = selectedPurchaseOrder?.quotation?.grandTotal ?? currentPaymentReq?.amount;
    const handleEditClick = (paymentReq: PaymentRequisition) => {
        setCurrentPaymentReq(paymentReq);
        setFormData({
            ...paymentReq
        });
        setIsDialogOpen(true);
    };

    const handleSubmit = async () => {
        // if (formData?.dueDate && formData?.referenceNumber && formData?.paymentMethod && formData.amount)
        try {

            if (currentPaymentReq && currentPaymentReq.id) {

                await updatePaymentReq({
                    id: currentPaymentReq.id,
                    paymentRequisitionPartial: {
                        ...({ priority: formData.priority }),
                        ...({ status: 'Prioritized' }),
                        ...({ swap: formData.swap }),
                        ...(formData.paymentMethod && { paymentMethod: formData.paymentMethod }),
                    }
                }).unwrap();
            }

            handleEditDialogClose();
        } catch (error) {
            console.error("Error saving payment requisition:", error);
        }
    };
    const handleEditDialogClose = () => {
        setIsDialogOpen(false);
        setCurrentPaymentReq(null);
        setFormData({
            paymentMethod: '',
            referenceNumber: '',
            amount: 0,
            rate: 1,
            dueDate: '',
            status: 'Awaiting Approval'
        });
    };

    //end form  patching

    const handleDialogOpen = async (procurementProcessId: string, documentType: string) => {
        setOpenDialog(null);

        const { data: updatedProcurementProcesses } = await triggerProcurementProcess({ procurementProcessId })

        if (updatedProcurementProcesses?.[0]) {
            const { data: updatedProcurementProcesses } = await triggerProcurementProcess({ procurementProcessId })
            if (updatedProcurementProcesses?.[0]) {
                const documentUrl = `${config.CDN_URL}/procurement/${documentType === 'purchaseOrder'
                    ? updatedProcurementProcesses[0].purchaseOrderDocument?.name
                    : updatedProcurementProcesses[0].requisitionDocument?.name
                    }`;

                if (documentUrl) {
                    setOpenDialog(documentUrl);
                } else {
                    console.error('Document URL not found');
                    setOpenDialog(null);
                }

            
            }



        } else {
            console.error('Procurement process not found');
            setOpenDialog(null);
        }
        setCurrentOpenRow(null);
    };

    const handleDialogClose = () => setOpenDialog(null);
    const closeAction = () => setCurrentOpenRow(null);
    const handleToggleDropdown = (rowId: string) => {
        setCurrentOpenRow(prev => (prev === rowId ? null : rowId));
    };

    const handleViewProcessDetails = (procurementProcessId: string) => {
        window.open(`/procurement/procurementProcess/${procurementProcessId}`, '_blank');
        setCurrentOpenRow(null);
    };

    const handleFinanceManagerApprove = async (procurementProcessId: string) => {
        if (!procurementProcessId) return;
        const result = await triggerProcurementProcess({ procurementProcessId });
        if (result && result.data && result.data[0].currentTask == 'CFOAllocateFunds') {
            window.open(`/procurement/CFOAllocateFunds/${procurementProcessId}`, '_blank');
        } else {
            alert('Not authorized, Process moved to a new Task')
        }
        setCurrentOpenRow(null);
    };

    if (isLoading) return <CircularProgress />;
    if (isError) return <div>Error fetching payment requisitions.</div>;

    const filteredPaymentRequisitions = (paymentRequisitions ?? []).filter((req: any) => {
        const reviewerEmail = req?.reviewedBy?.email || req?.reviewedBy?.user?.email || '';

        if (filters.reviewedByEmail === NOT_REVIEWED_OPTION && req?.reviewedBy) {
            return false;
        }

        if (
            filters.reviewedByEmail &&
            filters.reviewedByEmail !== NOT_REVIEWED_OPTION &&
            reviewerEmail !== filters.reviewedByEmail
        ) {
            return false;
        }

        if (filters.reviewedAtPeriod) {
            const reviewedAt = req?.reviewedAt ? new Date(req.reviewedAt) : null;
            const reviewedAtRange = getReviewedAtRange(filters.reviewedAtPeriod);
            if (!reviewedAt || !reviewedAtRange) {
                return false;
            }

            if (reviewedAt < reviewedAtRange.start || reviewedAt > reviewedAtRange.end) {
                return false;
            }
        }

        return true;
    });

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ padding: 3, borderRadius: 2, mb: 3, bgcolor: '#f9f9f9', boxShadow: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#333', fontWeight: 'bold' }}>
                    Payment Requests
                </Typography>

                <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                    <TextField
                        label="Supplier Name"
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                        value={filters.supplierName}
                        onChange={e => handleFilterChange('supplierName', e.target.value)}
                    />

                    <TextField
                        label="Requisition Number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
                        value={filters.reqNumber}
                        onChange={(e) => handleFilterChange('reqNumber', e.target.value)}
                    />
                    <TextField
                        label="Purchase Order Number"
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
                        value={filters.purchaseOrderNumber}
                        onChange={(e) => handleFilterChange('purchaseOrderNumber', e.target.value)}
                    />

                    <TextField
                        label="Development"
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                        value={filters.development}
                        onChange={e => handleFilterChange('development', e.target.value)}
                    />

                    <TextField
                        label="Project"
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                        value={filters.project}
                        onChange={e => handleFilterChange('project', e.target.value)}
                    />

                    <TextField
                        label="Department"
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                        value={filters.department}
                        onChange={e => handleFilterChange('department', e.target.value)}
                    />

                    <TextField
                        label="From Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                        value={filters.fromDate}
                        onChange={e => handleFilterChange('fromDate', e.target.value)}
                    />

                    <TextField
                        label="To Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                        value={filters.toDate}
                        onChange={e => handleFilterChange('toDate', e.target.value)}
                    />

                    <FormControl
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
                    >
                        <InputLabel id="currency-select-label">Currency</InputLabel>
                        <Select
                            labelId="currency-select-label"
                            value={filters.currency || ''}
                            label="Currency"
                            onChange={(e) => handleFilterChange('currency', e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="ZAR">ZAR</MenuItem>
                            <MenuItem value="ZIG">ZIG</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
                    >
                        <InputLabel id="payment-method-select-label">Payment Method</InputLabel>
                        <Select
                            labelId="payment-method-select-label"
                            value={filters.paymentMethod || ''}
                            label="Payment Method"
                            onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="Cash">Cash</MenuItem>
                            <MenuItem value="Nostro">Nostro</MenuItem>

                        </Select>
                    </FormControl>

                    <FormControl
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
                    >
                        <InputLabel id="status-select-label">Status</InputLabel>
                        <Select
                            labelId="status-select-label"
                            value={filters.status || ''}
                            label="Status"
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="Awaiting Approval">Awaiting Approval</MenuItem>
                            <MenuItem value="Approved">Approved</MenuItem>
                            <MenuItem value="Rejected">Rejected</MenuItem>
                            <MenuItem value="Scheduled">Scheduled</MenuItem>
                            <MenuItem value="Prioritized">Prioritized</MenuItem>
                            <MenuItem value="Paid">Paid</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
                    >
                        <InputLabel id="payment-type-select-label">Payment Type</InputLabel>
                        <Select
                            labelId="payment-type-select-label"
                            value={filters.paymentType || ''}
                            label="Payment Type"
                            onChange={(e) => handleFilterChange('paymentType', e.target.value)}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="DEPOSIT">DEPOSIT</MenuItem>
                            <MenuItem value="INTERIM">INTERIM</MenuItem>
                            <MenuItem value="FINAL PAYMENT">FINAL PAYMENT</MenuItem>
                            <MenuItem value="ONCE OFF">ONCE OFF</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
                    >
                        <InputLabel id="reviewed-by-select-label">Reviewed By</InputLabel>
                        <Select
                            labelId="reviewed-by-select-label"
                            value={filters.reviewedByEmail}
                            label="Reviewed By"
                            onChange={(e) => handleFilterChange('reviewedByEmail', e.target.value)}
                        >
                            <MenuItem value={NOT_REVIEWED_OPTION}>Not reviewed yet</MenuItem>
                            {financeManagers.map((manager) => (
                                <MenuItem key={manager.id || manager.email} value={manager.email}>
                                    {manager.name || manager.email}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
                    >
                        <InputLabel id="reviewed-at-period-label">Reviewed At</InputLabel>
                        <Select
                            labelId="reviewed-at-period-label"
                            value={filters.reviewedAtPeriod}
                            label="Reviewed At"
                            onChange={(e) =>
                                handleFilterChange('reviewedAtPeriod', e.target.value as ReviewedAtPeriod)
                            }
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="today">Today</MenuItem>
                            <MenuItem value="week">This Week</MenuItem>
                            <MenuItem value="month">This Month</MenuItem>
                            <MenuItem value="quarter">This Quarter</MenuItem>
                            <MenuItem value="year">This Year</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Request Amount"
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                        value={filters.amount}
                        onChange={e => handleFilterChange('amount', e.target.value)}
                    />


                    <FormControlLabel
                        control={
                            <Switch
                                checked={filters.isOutstandingPayment}
                                onChange={e => handleFilterChange('isOutstandingPayment', e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Show Outstanding Only"
                        sx={{ ml: 1 }}
                    />



                    <FormControlLabel
                        control={
                            <Switch
                                checked={reportByEmailEnabled}
                                onChange={e => {
                                    const isEnabled = e.target.checked;
                                    setReportByEmailEnabled(isEnabled);
                                    handleFilterChange('reportByEmail', isEnabled);
                                    if (!isEnabled) handleFilterChange('reportName', '');
                                }}
                                color="primary"
                            />
                        }
                        label="Email Report"
                    />

                    {reportByEmailEnabled && (
                        <TextField
                            label="Report Name"
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
                            value={filters.reportName}
                            onChange={e => handleFilterChange('reportName', e.target.value)}
                        />
                    )}
                </Box>

                <Stack direction="row" spacing={2} mt={2}>
                    <Button
                        variant="outlined"
                        size="large"
                        fullWidth
                        onClick={handleApplyFilters}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 'bold',
                            color: '#1976d2',
                            borderColor: '#1976d2',
                            '&:hover': { bgcolor: '#e3f2fd', borderColor: '#1565c0' },
                        }}
                    >
                        Apply Filters
                    </Button>
                    <PaymentRequisitionsExportButton
                        apiParams={apiParams}
                        reportLabel="Pending Review Payment Requisitions"
                        fileNamePrefix="pending-review-payment-requisitions"
                    />
                </Stack>
            </Box>

            <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#1976d2' }}>
                        <TableRow>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>PO number</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Title</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Development</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Due Date</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Supplier</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Method/Status</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reviewed By</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Action</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {filteredPaymentRequisitions.length > 0 ? (
                            filteredPaymentRequisitions.map((req: any) => {
                                const isOverdue = new Date(req.dueDate) < new Date();
                                const reviewedByName = req?.reviewedBy?.name || req?.reviewedBy?.user?.name;
                                const reviewedByJobTitle = req?.reviewedBy?.user?.jobTitle || req?.reviewedBy?.jobTitle;
                                const reviewedAt = req?.reviewedAt;
                                const hasReviewer = Boolean(req?.reviewedBy);

                                return (
                                    <TableRow key={req.id} hover>
                                        <TableCell>{req.purchaseOrder?.purchaseOrderNumber}</TableCell>
                                        <TableCell>{req.purchaseOrder?.title || req.title || 'N/A'}</TableCell>
                                        <TableCell>{req?.development || 'N/A'},{req?.project || 'N/A'},{req?.department || 'N/A'}</TableCell>



                                        <TableCell align="right">
                                            {isOverdue ? (
                                                <DangerTableCell>
                                                    <WarningIcon />
                                                    {new Date(req.dueDate).toLocaleDateString('en-GB', {
                                                        weekday: 'short',
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric'
                                                    })}
                                                </DangerTableCell>
                                            ) : (
                                                new Date(req.dueDate).toLocaleDateString()
                                            )}
                                        </TableCell>

                                        <TableCell>{req.purchaseOrder?.quotation?.supplier?.name || 'N/A'}</TableCell>

                                        <TableCell>{req.paymentMethod},{req.paymentType}

                                            <Box
                                                sx={{
                                                    bgcolor:
                                                        req.status === 'Paid' ? '#4caf50' :
                                                            req.status === 'Rejected' ? '#f44336' :
                                                                req.status === 'Approved' ? '#2196f3' :
                                                                    req.status === 'Prioritized' ? '#ffb300' :
                                                                        req.status === 'Scheduled' ? '#9c27b0' :
                                                                            '#ff9800',
                                                    color: 'white',
                                                    borderRadius: '4px',
                                                    px: 1,
                                                    py: 0.5,
                                                    display: 'inline-block',
                                                    fontWeight: 500,
                                                    textAlign: 'center'
                                                }}

                                            >
                                                {req.status}
                                            </Box>
                                        </TableCell>

                                        <TableCell sx={{ fontWeight: 'bold' }}>
                                            {req.currency} {req.amount?.toFixed(2)}
                                        </TableCell>

                                        <TableCell>
                                            {hasReviewer ? (
                                                <Box>
                                                    <Typography sx={{ fontWeight: 600 }}>
                                                        {reviewedByName || 'N/A'}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {reviewedByJobTitle || 'N/A'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {reviewedAt
                                                            ? new Date(reviewedAt).toLocaleString('en-GB', {
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })
                                                            : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2">Not reviewed yet</Typography>
                                            )}
                                        </TableCell>



                                        <TableCell align="right">
                                            <Box sx={{ position: "relative", display: "inline-block" }}>
                                                <Button
                                                    sx={{
                                                        bgcolor: "primary.main",
                                                        color: "white",
                                                        "&:hover": { bgcolor: "primary.dark" },
                                                    }}
                                                    onClick={() => handleToggleDropdown(req.id || "")}
                                                >
                                                    Actions
                                                </Button>

                                                {currentOpenRow === req.id && (
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            top: "100%",
                                                            right: 0,
                                                            zIndex: 1,
                                                            mt: 1,
                                                            minWidth: "150px",
                                                            bgcolor: "white",
                                                            boxShadow: 3,
                                                            borderRadius: 1,
                                                            overflow: "hidden",
                                                        }}
                                                    >

                                                        <IconButton
                                                            onClick={closeAction}
                                                            sx={{
                                                                position: "absolute",
                                                                top: 4,
                                                                right: 4,
                                                                bgcolor: "white",
                                                                border: "1px solid red",
                                                                color: "red",
                                                                "&:hover": { bgcolor: "grey.100" },
                                                            }}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>

                                                        <Button
                                                             sx={{
                                                                display: "block",
                                                                width: "100%",
                                                                textAlign: "left",
                                                                px: 2,
                                                                py: 1,
                                                                "&:hover": { bgcolor: "grey.100" },
                                                            }}
                                                            onClick={() => handleFinanceManagerApprove(req.purchaseOrder?.procurementProcessId || "")}
                                                        >
                                                            FM Approval
                                                        </Button>

                                                        <Button
                                                            sx={{
                                                                display: "block",
                                                                mt: 10,
                                                                width: "100%",
                                                                textAlign: "left",
                                                                px: 2,
                                                                py: 1,
                                                                "&:hover": { bgcolor: "grey.100" },
                                                            }}
                                                            onClick={() => handleViewProcessDetails(req.purchaseOrder?.procurementProcessId || "")}
                                                        >
                                                            Procurement Process
                                                        </Button>

                                                        <Button
                                                            sx={{
                                                                display: "block",
                                                                width: "100%",
                                                                textAlign: "left",
                                                                px: 2,
                                                                py: 1,
                                                                "&:hover": { bgcolor: "grey.100" },
                                                            }}
                                                            onClick={() => handleEditClick(req)}
                                                        >
                                                            Prioritize
                                                        </Button>

                                                        <Button
                                                            sx={{
                                                                display: "block",
                                                                width: "100%",
                                                                textAlign: "left",
                                                                px: 2,
                                                                py: 1,
                                                                "&:hover": { bgcolor: "grey.100" },
                                                            }}
                                                            onClick={() => handleDialogOpen(req.purchaseOrder?.procurementProcessId || '', 'purchaseOrder')}
                                                        >
                                                            View Purchase Order
                                                        </Button>

                                                        


                                                    </Box>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={11} align="center">
                                    No payment requisitions found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
                <Button
                    onClick={() => handlePageChange(Math.max(page - 1, 0))}
                    disabled={page === 0}
                    variant="contained"
                    sx={{ mr: 2 }}
                >
                    Previous
                </Button>

                <Typography variant="body1">Page {page + 1}</Typography>

                <Button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={filteredPaymentRequisitions.length < pageSize}
                    variant="contained"
                    sx={{ ml: 2 }}
                >
                    Next
                </Button>
            </Box>

            <Box mt={2} display="flex" justifyContent="center" alignItems="center">
                <Typography variant="body2" sx={{ mr: 2 }}>Rows per page:</Typography>
                <FormControl variant="outlined" size="small">
                    <Select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        sx={{ bgcolor: '#f0f4f7' }}
                    >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={20}>20</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <Dialog open={!!openDialog} onClose={handleDialogClose} fullWidth maxWidth="md">
                <Box p={2}>
                    {openDialog && (
                        <iframe
                            src={openDialog}
                            title="Purchase Order Document"
                            width="100%"
                            height="500px"
                            style={{ border: 'none' }}
                        />
                    )}
                    <Box textAlign="right" mt={2}>
                        <Button onClick={handleDialogClose} variant="contained" color="primary">
                            Close
                        </Button>
                    </Box>
                </Box>
            </Dialog>
            <Dialog open={isDialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    {currentPaymentReq ? 'Prioritize Requisition' : 'Create Payment Requisition'}
                </DialogTitle>
                <DialogContent>

                    <Card
                        elevation={3}
                        sx={{
                            mb: 3,
                            p: 2,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%)',
                            borderLeft: '4px solid #1976d2',
                            boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)',
                            transition: 'transform 0.3s, box-shadow 0.3s',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 24px 0 rgba(0,0,0,0.15)'
                            }
                        }}
                    >
                        <Box display="flex" alignItems="center" mb={1}>
                            <ReceiptLongIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    color: '#1976d2',
                                    letterSpacing: '0.5px'
                                }}
                            >
                                Payment Requisition Details
                            </Typography>
                        </Box>

                        <Divider sx={{ mb: 2, borderColor: 'rgba(25, 118, 210, 0.2)' }} />

                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={6}>
                                <Box display="flex" alignItems="center" mb={1.5}>
                                    <NumbersIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                    <Typography variant="body1">
                                        <Box component="span" fontWeight={600}>PO Number:</Box> {selectedPurchaseOrder?.purchaseOrderNumber || 'N/A'}
                                    </Typography>
                                </Box>

                                <Box display="flex" alignItems="center" mb={1.5}>
                                    <LocationOnIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                    <Typography variant="body1">
                                        <Box component="span" fontWeight={600}>Allocation:</Box>
                                        <Box component="span" display="block" ml={2}>
                                            {selectedPurchaseOrder?.development || currentPaymentReq?.development || 'N/A'},
                                        </Box>
                                        <Box component="span" display="block" ml={2}>
                                            {currentPaymentReq?.project || 'N/A'}
                                        </Box>
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Box display="flex" alignItems="center" mb={1.5}>
                                    <DescriptionIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                    <Typography variant="body1">
                                        <Box component="span" fontWeight={600}>Details:</Box> {selectedPurchaseOrder?.title || currentPaymentReq?.title || 'N/A'}
                                    </Typography>
                                </Box>

                                <Box display="flex" alignItems="center" mb={1.5}>
                                    <CurrencyExchangeIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                    <Typography variant="body1">
                                        <Box component="span" fontWeight={600}>Currency:</Box>
                                        <Box
                                            component="span"
                                            sx={{
                                                bgcolor: '#e3f2fd',
                                                px: 1,
                                                py: 0.5,
                                                borderRadius: 1,
                                                ml: 1
                                            }}
                                        >
                                            {paymentCurrency}
                                        </Box>
                                    </Typography>
                                </Box>

                                <Box display="flex" alignItems="center">
                                    <AttachMoneyIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                                    <Typography variant="body1">
                                        <Box component="span" fontWeight={600}>PO Value:</Box>
                                        <Box
                                            component="span"
                                            sx={{
                                                fontSize: '1.1rem',
                                                fontWeight: 700,
                                                color: '#2e7d32',
                                                ml: 1
                                            }}
                                        >
                                            {typeof paymentReferenceValue === 'number' ? paymentReferenceValue.toFixed(2) : 'N/A'}
                                        </Box>
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Card>


                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6} md={4}>
                            <TextField
                                label="Priority"
                                fullWidth
                                type="number"
                                value={formData.priority || 0}
                                onChange={(e) => {
                                    let value = Math.floor(Number(e.target.value));
                                    if (isNaN(value)) value = 0;
                                    value = Math.max(0, Math.min(10, value));
                                    handleFormChange('priority', value);
                                }}
                                inputProps={{
                                    min: 0,
                                    max: 10,
                                    step: 1,
                                    pattern: "^[0-9]|10$",
                                }}
                                required
                            />
                        </Grid>

                        <Grid item xs={6} md={8}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.swap === true}
                                        onChange={(e) =>
                                            handleFormChange('swap', e.target.checked)
                                        }
                                        color="primary"
                                    />
                                }
                                label={
                                    <Box display="flex" alignItems="center">
                                        <Typography variant="body1">Swap priority</Typography>
                                        <Box
                                            sx={{
                                                ml: 1,
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                backgroundColor: formData.swap === true ? '#4caf50' : '#f44336',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontSize: 12,
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {formData.swap === true ? 'YES' : 'NO'}
                                        </Box>
                                    </Box>
                                }
                                sx={{
                                    ml: 0,
                                    padding: '8px 16px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '4px',
                                    backgroundColor: '#f9f9f9'
                                }}
                            />
                        </Grid>
                    </Grid>
                    <Box display="flex" gap={2} mt={2}>
                        <TextField
                            select
                            label="Payment Method"
                            fullWidth
                            value={formData.paymentMethod || ''}
                            onChange={(e) => handleFormChange('paymentMethod', e.target.value)}
                            required
                        >
                            <MenuItem value="Nostro">Nostro</MenuItem>
                            <MenuItem value="Cash">Cash</MenuItem>
                        </TextField>

                        <TextField
                            label="Amount"
                            fullWidth
                            type="number"
                            value={formData.amount || 0}
                            onChange={(e) => handleFormChange('amount', parseFloat(e.target.value))}
                            inputProps={{ min: 0, step: 0.01 }}
                            required
                            InputProps={{
                                readOnly: true,
                            }}
                        />

                        <TextField
                            label="Rate"
                            fullWidth
                            type="number"
                            value={formData.rate || 0}
                            onChange={(e) => handleFormChange('rate', parseFloat(e.target.value))}
                            inputProps={{ min: 0, step: 0.01 }}
                            required
                            InputProps={{
                                readOnly: true,
                            }}
                        />


                        <TextField
                            label="USD Equivalent"
                            fullWidth
                            value={
                                formData.amount && formData.rate && formData.rate > 0
                                    ? (formData.amount / formData.rate).toFixed(2)
                                    : ''
                            }
                            InputProps={{
                                readOnly: true,
                            }}
                        />


                    </Box>

                    <Box mt={2}>

                        {/* <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Due Date"
                                value={formData.dueDate}
                                onChange={(date) => handleFormChange('dueDate', date)}
                                renderInput={(params) => <TextField {...params} fullWidth required />}
                                minDate={new Date()}
                            />
                        </LocalizationProvider> */}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditDialogClose}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={!formData.paymentMethod || !formData.amount || !formData.dueDate}
                    >
                        {currentPaymentReq ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default PaymentRequisitionList;